import React, { useEffect, useState } from 'react';
import { get } from 'lodash';
import axios from 'axios';
import moment from 'moment';
import {
	SdfAlert,
	SdfBox,
	SdfButton,
	SdfFocusPane,
	SdfIcon,
	SdfPromoBox,
	SdfTooltip
} from '@synerg/react-components';

export default function PreAuthEmail({ activeCR }) {
	const [timeStampData, setTimeStampData] = useState(null);
	const [lastSentEmailTimeStamp, setLastSentEmailTimeStamp] = useState('');
	const [showLastSentModal, setShowLastSentModal] = useState(false);
	const [disableEmailButton, setDisableEmailButton] = useState(true);
	const [showInfoIcon, setShowInfoIcon] = useState(false);
	const [showSuccessMessage, setShowSuccessMessage] = useState(false);
	const [showErrorMessage, setShowErrorMessage] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');

	const date = get(timeStampData, 'date', '');
	const time = get(timeStampData, 'time', '');

	const id = activeCR.consumerApplicationID;
	const requestNumber = activeCR.requestNumber;
	const cutOffDate = activeCR.cutOffDate;

	const hideModal = () => {
		setShowLastSentModal(false);
	};

	const onClickSendReminderBtn = () => {
		if (timeStampData.hasTimeStamp && !timeStampData.isSentToday) {
			setShowLastSentModal(true);
		} else {
			sendReminderEmail();
		}
	};

	const sendEmail = () => {
		sendReminderEmail();
		setShowLastSentModal(false);
	};

	const getEmailTimeStampData = async () => {
		try {
			const { data: responseObject } = await axios.get(
				`${import.meta.env.VITE_ROOT_PATH}/api/reminder-email/time-stamp/${id}/${requestNumber}`
			);

			const date = moment(responseObject.lastEmailSent).utc().local().format('MM/DD/YYYY');
			const time = moment(responseObject.lastEmailSent).utc().local().format('hh:mm A');
			setTimeStampData({ ...responseObject, date, time });

			if (responseObject.hasTimeStamp && !responseObject.isSentToday) {
				setDisableEmailButton(undefined);
				setLastSentEmailTimeStamp(`${date} at ${time}`);
			} else if (!responseObject.hasTimeStamp && !responseObject.isSentToday) {
				setDisableEmailButton(undefined);
			} else if (responseObject.hasTimeStamp && responseObject.isSentToday) {
				setDisableEmailButton(true);
				setShowInfoIcon(true);
			}
		} catch (e) {
			hideModal();
			setShowErrorMessage(true);
			setErrorMessage(get(e, 'response.data.message', 'Something went wrong when trying to get last email timestamp. Please try again.'));
		}
	};

	const sendReminderEmail = async () => {
		try {
			setDisableEmailButton(true);

			await axios.post(
				`${import.meta.env.VITE_ROOT_PATH}/api/reminder-email`, {
					b2bConsumerAppID: id,
					cutOffDate,
					requestNumber
				});
			setShowSuccessMessage(true);
		} catch (e) {
			setShowErrorMessage(true);
			setErrorMessage('Error occurred, please try again.');
		}
	};

	useEffect(() => {
		if (activeCR.consumerApplicationID && activeCR.requestNumber) {
			getEmailTimeStampData();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeCR]);

	return (
		<div>
			{showSuccessMessage &&
				<SdfAlert
					size="sm"
					status="success"
					aria-live="polite"
					closeable="true"
					toast={true}
					onSdfCloseComplete={() => setShowSuccessMessage(false)}	
				>
					Email successfully sent
				</SdfAlert>
			}
			{showErrorMessage &&
				<SdfAlert
					size="sm"
					status="error"
					aria-live="polite"
					closeable="true"
					toast={true}
					onSdfCloseComplete={() => {
						setShowErrorMessage(false);
						setErrorMessage('');
					}}	
				>
					{errorMessage}
				</SdfAlert>
			}
			<SdfButton
				emphasis="tertiary"
				// @synerg/react-components v3.8.0 --> values true or undefined works for disabled, not false
				disabled={disableEmailButton}
				active={false}
				icon="action-email"
				variant="text"
				onClick={() => onClickSendReminderBtn()}
			>
				Send preauthorization email
			</SdfButton>
			{showInfoIcon && <SdfTooltip state="neutral" positionContext="ELEMENT">
				<SdfIcon
					class="text-alert-neutral text-xs mb-1 ml-1"
					slot="tooltip-target"
					icon="action-help"
				/>
				Only one email can be sent per day. The last email was sent on {date} at {time}
			</SdfTooltip>}
			<SdfFocusPane
				paneType="floating"
				visible={showLastSentModal}
				size="md"
				closeable={true}
				dismissOnClickOutside={true}
				hideAcceptButton={true}
				hideDismissButton={true}
				hideHeader={true}
				onSdfDismiss={hideModal}
			>
				<div className="flex pr-4">
					<SdfPromoBox
						size='md'
						layout='vertical'
						variant="clear"
						align='left'
						spacing='tight'
						illustration-name={'warning'}
					/>
					<div className="px-4">
						<div className="text-heading-05 font-bold py-4">Are you sure?</div>
						<div className="text-heading-02 font-bold pb-4">
							This action will send out a mass email reminder to all customers who still need to provide pre-authorization.
						</div>
						<SdfBox variant="filled">
							<div className="text-heading-02 font-bold">The last email was sent on {lastSentEmailTimeStamp}.</div>
						</SdfBox>
						<div className="text-heading-02 font-bold py-4">Would you still like to send?</div>
					</div>
				</div>
				<div slot="custom-buttons" className="pr-2 pb-2">
					<SdfButton className="pr-3" onClick={() => hideModal()}>Cancel</SdfButton>
					<SdfButton emphasis="primary" onClick={() => sendEmail()}>Yes</SdfButton>
				</div>
			</SdfFocusPane>
		</div>
	)
}
